import { Form } from "@/components/organisms/Form";
import { request } from "@/common/api";
import { Buttons } from "@/components/atoms/Buttons";
import { showAlert } from "@/components/containers/Alert";
import { useNavigate } from "react-router";
import { useMutation } from "@tanstack/react-query";
import { PreviewImage } from "@/components/atoms/PreviewImage";
import { MainTemplate } from "@/components/templates/MainTemplate";
import { photoAgreeState } from "@/common/states/agree";
import { spineImageState } from "@/common/states/image";
import { useEffect, useRef } from "react";
import { ReactComponent as IconArrow } from "@/styles/assets/svg/icon_arrow.svg";
import { useRecoilState, useRecoilValue } from "recoil";

export const PhotoPreviewPage = () => {
  const agree = useRecoilValue(photoAgreeState);
  const imgRef = useRef<any>();
  const navigate = useNavigate();
  const [image, setImage] = useRecoilState(spineImageState);

  useEffect(() => {
    if (!agree || !image) {
      navigate("/");
    }
  }, [agree, image, navigate]);

  const { mutateAsync: imageUpload, isPending } = useMutation({
    mutationFn: (data: FormData) =>
      request({
        method: "POST",
        url: "/spine-image/upload",
        data: data,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }),
    onSuccess: (data: any) => {
      if (data.success) {
        navigate("/spine/photo-done", { replace: true });
      } else {
        showAlert(data.message);
      }
    },
  });

  const handleSubmit = async () => {
    const formData = new FormData();
    formData.append("files", image!);
    formData.append("agreementTime", JSON.stringify(agree));
    imageUpload(formData);
  };

  const handleRetake = () => {
    if (window.WebViewChannel) {
      window.WebViewChannel.postMessage(
        JSON.stringify({
          type: "OPEN_GALLERY",
          payload: {
            nextPath: "/spine/photo-preview",
          },
        })
      );
    } else {
      imgRef.current.click();
    }
  };

  const onChangeImg = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    try {
      const imgFile = e.target.files[0];
      setImage(imgFile);
    } catch (error) {
      showAlert("이미지 촬영에 실패하였습니다. 다시 시도해주세요.");
    }
  };

  useEffect(() => {
    const handleImageSelected = async (event: any) => {
      try {
        const { base64, fileName } = event.detail;

        const response = await fetch(base64);
        const blob = await response.blob();
        const file = new File([blob], fileName, { type: "image/jpeg" });

        setImage(file);
      } catch (error) {
        showAlert("이미지 처리에 실패하였습니다. 다시 시도해주세요.");
      }
    };

    window.addEventListener("imageSelected", handleImageSelected);
    return () =>
      window.removeEventListener("imageSelected", handleImageSelected);
  }, [setImage]);

  return (
    <MainTemplate
      nav={false}
      subPage={true}
      menuBtn={false}
      buttonLeft={
        <Buttons
          type="button"
          size="headerTextIcon"
          layout="text"
          color="bright100"
          icon={<IconArrow />}
          label="건강"
          onClick={() => navigate("/spine")}
        />
      }
      pageTitle="척추 사진 촬영"
    >
      <Form
        submitBtn={
          <Buttons
            type="button"
            size="lg"
            layout="solid"
            color="primary100"
            label="사진 등록"
            disabled={isPending}
            onClick={handleSubmit}
          />
        }
        backBtn={
          <Buttons
            type="button"
            size="lg"
            layout="text"
            color="primary100"
            label="재등록"
            onClick={handleRetake}
          />
        }
      >
        <>
          <PreviewImage image={image} />
          <input
            id="cameraFile"
            ref={imgRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={onChangeImg}
          />
        </>
      </Form>
    </MainTemplate>
  );
};
